import { FormHelperText, Input, Option, Select, Textarea } from '@healthinal/ui';
import isEmail from 'is-email';
import { ReactNode } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { PatientDtoGender, PatientDtoResidencyPermit } from '../../../api/generated/model';
import { ReferencePersonDto } from '../../../api/generated/model/bezugspersonDto.ts';
import { PersonLivingStatus } from '../../../api/generated/person-controller/person-controller.ts';
import type { DuplicateCheckResponse } from '../../../api/generated/person/checkDuplicate.ts';
import type { CreatePersonFormValues } from '../../../api/generated/person/createPerson.ts';
import { GermanDateInput } from '../../../components/form/GermanDateInput.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../components/form/SingeLineFormComponents.tsx';
import { PostalCodeFields } from '../../../components/PostalCodeFields.tsx';
import { getCurrentDate } from '../../../utils/date.ts';
import { getGenderName } from '../../../utils/gender.ts';
import {
  DuplicateField,
  getDuplicateFieldArticle,
  getDuplicateFieldLabel,
} from '../../../utils/getDuplicatePersonField.ts';
import { getLivingStatusName } from '../../../utils/livingStatus.ts';
import { isPhoneNumberValid } from '../../../utils/phoneNumber.ts';
import { getResidencyPermitName } from '../../../utils/residencyPermit.ts';
import type { ReferencePersonCreateEndpoint } from '../../components/bezugsperson/CreateReferencePersonDialog.tsx';
import { SelectReferencePersonMulti } from '../../components/bezugsperson/SelectReferencePersonMulti.tsx';
import { SelectCountry } from '../../components/SelectCountry.tsx';
import { SelectLanguage } from '../../components/SelectLanguage.tsx';

export type PersonDataFormValues = Omit<CreatePersonFormValues, 'bezugspersonen'> & {
  bezugspersonen: ReferencePersonDto[];
};

export type DuplicateErrors = Partial<Record<DuplicateField, string>>;
export const duplicateCheckFields = ['firstName', 'lastName', 'birthDate', 'asylumNumber', 'zemisNumber'] as const;
export type DuplicateCheckField = (typeof duplicateCheckFields)[number];

const fieldToResponseKey: Record<DuplicateField, keyof DuplicateCheckResponse> = {
  firstName: 'firstNameMatches',
  lastName: 'lastNameMatches',
  birthDate: 'birthDateMatches',
  asylumNumber: 'asylumNumberMatches',
  zemisNumber: 'zemisNumberMatches',
  address: 'addressMatches',
};

export function getDuplicateCheckPayload(
  field: DuplicateCheckField,
  value: string | null | undefined,
  currentValues: PersonDataFormValues,
) {
  const normalizedValue = typeof value === 'string' ? value.trim() : value;

  return duplicateCheckFields.reduce<Partial<Record<DuplicateCheckField, string>>>((acc, key) => {
    const rawValue = key === field ? normalizedValue : currentValues[key];
    const nextValue = typeof rawValue === 'string' ? rawValue.trim() : '';

    if (nextValue) {
      acc[key] = nextValue;
    }

    return acc;
  }, {});
}

export function getDuplicateErrors(
  payload: Partial<Record<DuplicateCheckField, string>>,
  response: DuplicateCheckResponse,
  ignoredPersonId?: string,
): DuplicateErrors {
  return duplicateCheckFields.reduce<DuplicateErrors>((acc, key) => {
    const matches = response[fieldToResponseKey[key]].filter((match) => match.id !== ignoredPersonId);

    if (payload[key] && matches.length > 0) {
      acc[key] = `${getDuplicateFieldArticle(key)} ${getDuplicateFieldLabel(key)} existiert bereits`;
    }

    return acc;
  }, {});
}

type ReferencePersonSelectConfig =
  | {
      mode: 'create';
      fetchedOptions?: ReferencePersonDto[];
    }
  | {
      mode: 'edit';
      personId: string;
      createEndpoint?: ReferencePersonCreateEndpoint;
      fetchedOptions: ReferencePersonDto[];
    };

export function PersonDataFormFields({
  register,
  control,
  setValue,
  getValues,
  getFieldState: getOriginalFieldState,
  errors,
  duplicateErrors,
  checkDuplicateField,
  referencePersonSelect,
  postalCodeEnabled = true,
  showPlaceholders = false,
  showDirtyIndicator = true,
  contactPatientRelationshipStatusField,
}: {
  register: UseFormRegister<PersonDataFormValues>;
  control: Control<PersonDataFormValues>;
  setValue: UseFormSetValue<PersonDataFormValues>;
  getValues: UseFormGetValues<PersonDataFormValues>;
  getFieldState: UseFormGetFieldState<PersonDataFormValues>;
  errors: FieldErrors<PersonDataFormValues>;
  duplicateErrors: DuplicateErrors;
  checkDuplicateField: (field: DuplicateCheckField, value: string | null | undefined) => void | Promise<void>;
  referencePersonSelect: ReferencePersonSelectConfig;
  postalCodeEnabled?: boolean;
  showPlaceholders?: boolean;
  showDirtyIndicator?: boolean;
  contactPatientRelationshipStatusField?: ReactNode;
}) {
  const getFieldState: UseFormGetFieldState<PersonDataFormValues> = (...args) => {
    const fieldState = getOriginalFieldState(...args);

    return showDirtyIndicator ? fieldState : { ...fieldState, isDirty: false };
  };
  const firstNameRegistration = register('firstName', { required: true });
  const lastNameRegistration = register('lastName', { required: true });
  const asylumNumberRegistration = register('asylumNumber');
  const zemisNumberRegistration = register('zemisNumber');
  const birthDateMax = getCurrentDate();

  return (
    <SingeLineFormGrid>
      <SingleLineFormControl<PersonDataFormValues> label="Anrede" fieldState={getFieldState('salutation')}>
        <Controller
          name="salutation"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              onChange={(_, value) => onChange(value ?? '')}
              value={value || null}
              variant="plain"
              placeholder="Bitte auswählen">
              <Option value="MR">Herr</Option>
              <Option value="MRS">Frau</Option>
            </Select>
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Nachname" required fieldState={getFieldState('lastName')}>
        <Input
          {...lastNameRegistration}
          variant="plain"
          placeholder={showPlaceholders ? 'Nachname' : undefined}
          onBlur={(event) => {
            void lastNameRegistration.onBlur(event);
            void checkDuplicateField('lastName', event.target.value);
          }}
        />
        {errors.lastName && <FormHelperText>Nachname muss ausgefüllt werden</FormHelperText>}
        {!errors.lastName && duplicateErrors.lastName && <FormHelperText>{duplicateErrors.lastName}</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Vorname" required fieldState={getFieldState('firstName')}>
        <Input
          {...firstNameRegistration}
          variant="plain"
          placeholder={showPlaceholders ? 'Vorname' : undefined}
          onBlur={(event) => {
            void firstNameRegistration.onBlur(event);
            void checkDuplicateField('firstName', event.target.value);
          }}
        />
        {errors.firstName && <FormHelperText>Vorname muss ausgefüllt werden</FormHelperText>}
        {!errors.firstName && duplicateErrors.firstName && <FormHelperText>{duplicateErrors.firstName}</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues>
        label="Geburtsdatum"
        required
        fieldState={getFieldState('birthDate')}>
        <GermanDateInput
          dateInputMode="formatted"
          control={control}
          name="birthDate"
          rules={{
            required: true,
            max: { value: birthDateMax, message: 'range' },
          }}
          max={birthDateMax}
          variant="plain"
          onValueBlur={(value) => void checkDuplicateField('birthDate', value)}
        />
        {errors.birthDate && (
          <FormHelperText>
            {errors.birthDate.type === 'required'
              ? 'Geburtsdatum muss ausgefüllt werden'
              : 'Geburtsdatum darf nicht in der Zukunft liegen'}
          </FormHelperText>
        )}
        {!errors.birthDate && duplicateErrors.birthDate && <FormHelperText>{duplicateErrors.birthDate}</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Geschlecht" fieldState={getFieldState('gender')}>
        <Controller
          name="gender"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select onChange={(_, value) => onChange(value)} value={value} variant="plain">
              {Object.values(PatientDtoGender).map((gender) => (
                <Option value={gender} key={gender}>
                  {getGenderName(gender)}
                </Option>
              ))}
            </Select>
          )}
        />
      </SingleLineFormControl>

      {contactPatientRelationshipStatusField}

      <SingleLineFormControl<PersonDataFormValues>
        label="Telefon Nr. Mobil"
        fieldState={getFieldState('mobilePhoneNumber')}>
        <Input
          {...register('mobilePhoneNumber', {
            validate: (mobilePhoneNumber) => !mobilePhoneNumber || isPhoneNumberValid(mobilePhoneNumber),
          })}
          type="tel"
          variant="plain"
        />
        {errors.mobilePhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues>
        label="Telefon Nr. Privat"
        fieldState={getFieldState('privatePhoneNumber')}>
        <Input
          {...register('privatePhoneNumber', {
            validate: (privatePhoneNumber) => !privatePhoneNumber || isPhoneNumberValid(privatePhoneNumber),
          })}
          type="tel"
          variant="plain"
        />
        {errors.privatePhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues>
        label="Telefon Nr. Geschäft"
        fieldState={getFieldState('workPhoneNumber')}>
        <Input
          {...register('workPhoneNumber', {
            validate: (workPhoneNumber) => !workPhoneNumber || isPhoneNumberValid(workPhoneNumber),
          })}
          type="tel"
          variant="plain"
        />
        {errors.workPhoneNumber && <FormHelperText>Diese Telefon Nr. ist nicht korrekt</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="E-Mail-Adresse" fieldState={getFieldState('email')}>
        <Input
          {...register('email', {
            validate: (email) => !email || isEmail(email),
          })}
          type="email"
          variant="plain"
        />
        {errors.email && <FormHelperText>Diese E-Mail-Adresse ist nicht korrekt</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Nationalität" fieldState={getFieldState('nationality')}>
        <Controller
          name="nationality"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SelectCountry onChange={onChange} value={value} variant="plain" />
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Herkunftsland" fieldState={getFieldState('origin')}>
        <Controller
          name="origin"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SelectCountry onChange={onChange} value={value} variant="plain" />
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Einreisedatum" fieldState={getFieldState('arrival')}>
        <Input {...register('arrival')} variant="plain" />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="FREPO-Status" fieldState={getFieldState('residencyPermit')}>
        <Controller
          name="residencyPermit"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select onChange={(_, value) => onChange(value)} value={value} variant="plain">
              {Object.values(PatientDtoResidencyPermit).map((permit) => (
                <Option value={permit} key={permit}>
                  {getResidencyPermitName(permit)}
                </Option>
              ))}
            </Select>
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="N-Nummer" fieldState={getFieldState('asylumNumber')}>
        <Input
          {...asylumNumberRegistration}
          variant="plain"
          onBlur={(event) => {
            void asylumNumberRegistration.onBlur(event);
            void checkDuplicateField('asylumNumber', event.target.value);
          }}
        />
        {duplicateErrors.asylumNumber && <FormHelperText>{duplicateErrors.asylumNumber}</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="ZEMIS-Nummer" fieldState={getFieldState('zemisNumber')}>
        <Input
          {...zemisNumberRegistration}
          variant="plain"
          onBlur={(event) => {
            void zemisNumberRegistration.onBlur(event);
            void checkDuplicateField('zemisNumber', event.target.value);
          }}
        />
        {duplicateErrors.zemisNumber && <FormHelperText>{duplicateErrors.zemisNumber}</FormHelperText>}
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Sprachen" fieldState={getFieldState('languages')}>
        <Controller
          name="languages"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SelectLanguage value={value ?? []} onChange={onChange} variant="plain" />
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Zusatz" fieldState={getFieldState('additionalInformation')}>
        <Input {...register('additionalInformation')} variant="plain" />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Strasse" fieldState={getFieldState('street')}>
        <Input {...register('street')} variant="plain" />
      </SingleLineFormControl>

      <PostalCodeFields<PersonDataFormValues>
        control={control}
        setValue={setValue}
        getValues={getValues}
        getFieldState={getFieldState}
        zipName="zip"
        cityName="city"
        cantonName="canton"
        zipLabel="PLZ"
        cityLabel="Ort"
        cantonLabel="Kanton"
        enabled={postalCodeEnabled}
        layout="singleLine"
        variant="plain"
      />

      <SingleLineFormControl<PersonDataFormValues> label="Bezugsperson" fieldState={getFieldState('bezugspersonen')}>
        <Controller
          name="bezugspersonen"
          control={control}
          render={({ field: { value, onChange } }) =>
            referencePersonSelect.mode === 'edit' ? (
              <SelectReferencePersonMulti
                mode="edit"
                personId={referencePersonSelect.personId}
                createEndpoint={referencePersonSelect.createEndpoint}
                fetchedOptions={referencePersonSelect.fetchedOptions}
                value={value ?? []}
                onChange={onChange}
                variant="plain"
              />
            ) : (
              <SelectReferencePersonMulti
                mode="create"
                fetchedOptions={referencePersonSelect.fetchedOptions}
                value={value ?? []}
                onChange={onChange}
                variant="plain"
              />
            )
          }
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Lebensstatus" fieldState={getFieldState('livingStatus')}>
        <Controller
          name="livingStatus"
          control={control}
          render={({ field: { onChange, value } }) => (
            <Select
              onChange={(_, value) => onChange(value)}
              value={value}
              variant="plain"
              placeholder="Bitte auswählen">
              {Object.values(PersonLivingStatus).map((status) => (
                <Option value={status} key={status}>
                  {getLivingStatusName(status)}
                </Option>
              ))}
            </Select>
          )}
        />
      </SingleLineFormControl>

      <SingleLineFormControl<PersonDataFormValues> label="Bemerkungen" fieldState={getFieldState('note')}>
        <Textarea {...register('note')} minRows={3} maxRows={6} variant="plain" />
      </SingleLineFormControl>
    </SingeLineFormGrid>
  );
}
